import React from 'react';
import styled from 'styled-components';
import SkeletonShape from 'components/skeleton-shape';
import { primary } from 'styles/colors';
import { STATUS } from 'utils/imageLoader';
import useImage from './use-image';

const Strip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ThumbBox = styled.button`
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  padding: 0;
  border: none;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background-color: #2a2a2a;
  opacity: 0.45;
  transition: opacity 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    opacity: 0.8;
  }
  &.active {
    opacity: 1;
    box-shadow: 0 0 0 2px ${primary};
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ThumbLoader = styled(SkeletonShape)`
  width: 100%;
  height: 100%;
  border-radius: 0px;
`;

const Thumb = ({ url, index, active, visible, onSelect }) => {
  const { status } = useImage(url, { priority: 10, enabled: visible });
  return (
    <ThumbBox
      type="button"
      data-index={index}
      className={active ? 'active' : ''}
      onClick={() => onSelect(index)}
      aria-label={`View image ${index + 1}`}>
      {status === STATUS.loaded ? (
        <img src={url} alt={`Thumbnail ${index + 1}`} />
      ) : (
        <ThumbLoader background="#2a2a2a" />
      )}
    </ThumbBox>
  );
};

export default ({ urls, activeIndex, onSelect }) => {
  const stripRef = React.useRef(null);
  const [visible, setVisible] = React.useState(() => new Set());

  // Lazily enqueue thumbnails as they come near the strip's viewport — combined with
  // the loader's concurrency cap this keeps large galleries from flooding the proxy.
  React.useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const observer = new IntersectionObserver(
      (observedEntries) => {
        const revealed = observedEntries.filter((entry) => entry.isIntersecting);
        if (!revealed.length) return;
        revealed.forEach((entry) => observer.unobserve(entry.target));
        setVisible((prev) => {
          const next = new Set(prev);
          revealed.forEach((entry) => next.add(Number(entry.target.dataset.index)));
          return next;
        });
      },
      { root: strip, rootMargin: '0px 300px' }
    );
    strip.querySelectorAll('[data-index]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [urls]);

  // Keep the active thumbnail centered
  React.useEffect(() => {
    const strip = stripRef.current;
    const el = strip?.querySelector(`[data-index='${activeIndex}']`);
    if (!strip || !el) return;
    strip.scrollTo({
      left: el.offsetLeft - strip.clientWidth / 2 + el.clientWidth / 2,
      behavior: 'smooth'
    });
  }, [activeIndex]);

  return (
    <Strip ref={stripRef}>
      {urls.map((url, index) => (
        <Thumb
          key={`${url}-${index}`}
          url={url}
          index={index}
          active={index === activeIndex}
          visible={visible.has(index)}
          onSelect={onSelect}
        />
      ))}
    </Strip>
  );
};
